import { Button } from '@/components/primitives/Button';
/**
 * EmailDraftForm — input form for the Email Helper
 * (Phase X.3.4 → X.3.4.8 → X.6.4B design-refresh reskin).
 *
 * X.6.4B: visual reskin (token fields, sage focus ring, pepper Generate button
 * in SPICE) + the mode toggle is now CONTROLLED by the page (so the workspace
 * header chip + output panel can react to SPICE). The fields, validation,
 * generation mutation, streaming, and onDraftReady payload are unchanged.
 *
 * 3 fields: recipient, topic, optional special instructions. The snapshot
 * omits `constraints` entirely when the optional field is empty.
 */

import { type ReactNode, useEffect, useRef, useState } from 'react';
import { KeptWorkNotice } from '@/components/KeptWorkNotice';
import { keptWorkKey, useKeptWork } from '@/lib/keptWork';
import { useMutation } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { aiToolsApi, type ApiError, type EmailDraftResponse } from '@/lib/api';
import { aiGenerateKeyOf } from '@/lib/errorCopy';
import { ErrorNotice } from '@/components/ErrorNotice';
import { EmailModeToggle, type EmailMode } from './EmailModeToggle';
import { EmailToneSelector, type EmailTone } from './EmailToneSelector';
import { TextTooLongNote, isTextTooLong } from './TextTooLongNote';
import { readAiInputMaxChars, type AiInputTool } from '@/lib/aiInputLimits';
import { aiToolFailureCopy, aiToolFailureOf, type AiToolFailure } from '@/lib/aiTextTooLong';
import { useInvalidateOnCeilingRefusal } from '@/hooks/useInvalidateOnCeilingRefusal';

export interface EmailDraftFormProps {
  hasDraft?: boolean;
  tenantSlug: string;
  /** Controlled mode (lifted to the page in X.6.4B). */
  mode: EmailMode;
  onModeChange: (mode: EmailMode) => void;
  /** Controlled Care-Management tone register (lifted to the page in B2). */
  tone: EmailTone;
  onToneChange: (tone: EmailTone) => void;
  /** Fired on generation start/finish so the page can show a drafting state. */
  onGeneratingChange?: (generating: boolean) => void;
  onDraftReady: (
    response: EmailDraftResponse,
    formInput: FormInputSnapshot,
    mode: EmailMode,
  ) => void;
  /**
   * The mode is fixed, so the Care Management <-> SPICE toggle leaves the
   * form: a client is already chosen for this visit (a SPICE door or the
   * picker), or the viewer is SPICE-only and has no other face.
   */
  modeLocked?: boolean;
  /** Seeds "Email Content" from the client's last plan, when one is
   *  known (the SPICE doors carry it along); the therapist edits from
   *  there like any other field. */
  initialTopic?: string;
  /** Seeds "Who is this for?" from the client's emergency contact's or
   *  POA's first name, when a door or the picker already had one on
   *  hand (never a new decrypting read); blank otherwise, same as today. */
  initialRecipient?: string;
  /** A client picked while the form is already on screen: its plan line
   *  and contact first name go into "Email Content" and "Who is this
   *  for?". Each replaces its field only while the field is empty or
   *  still holds the previous seed exactly as seeded (a door's initial
   *  value counts as one), so one client's plan never rides into another
   *  client's email and text the writer typed or edited is never
   *  replaced. `id` tells two picks of the same client apart. */
  seed?: EmailFormSeed | null;
  /** The longest Email Content or Special instructions the helper takes
   *  (characters, after trimming), read by the page from its tenant
   *  config; the factory value until the page passes one. */
  maxChars?: number;
  /** The longest "Who is this for?" the helper takes, read by the page
   *  from its tenant config (the `recipient` entry); the factory value
   *  until the page passes one. */
  recipientMaxChars?: number;
}

export interface EmailFormSeed {
  id: number;
  topic: string | null;
  recipient: string | null;
}

export interface FormInputSnapshot {
  recipient: string;
  topic: string;
  constraints?: string;
  // B2: the Care-Management tone register, captured so a reopened/regenerated
  // draft remembers it. Always present: the register the care manager
  // picked for Care Management, 'standard' for SPICE.
  tone: EmailTone;
}

const FIELD_CLS =
  'mt-1.5 block w-full min-h-11 rounded-[12px] border border-sage-400/20 bg-paper px-3.5 py-2.5 text-sm text-ink placeholder:text-sage-300 outline-none transition-[border-color,box-shadow] duration-[var(--dur-base)] ease-quiet focus:border-sage-400 focus:ring-[3px] focus:ring-sage-800/[6%]';
const LABEL_CLS = 'block text-[0.8125rem] font-semibold text-sage-800';
const HELP_CLS = 'mt-0.5 text-xs leading-relaxed text-ink-soft';

export function EmailDraftForm({
  hasDraft = false,
  tenantSlug,
  mode,
  onModeChange,
  tone,
  onToneChange,
  onGeneratingChange,
  onDraftReady,
  modeLocked = false,
  initialTopic = '',
  initialRecipient = '',
  seed = null,
  maxChars = readAiInputMaxChars(undefined, 'email'),
  recipientMaxChars = readAiInputMaxChars(undefined, 'recipient'),
}: EmailDraftFormProps): ReactNode {
  const [recipient, setRecipient] = useState(initialRecipient);
  const [topic, setTopic] = useState(initialTopic);
  // The last value each field was seeded with, as seeded. A field that
  // still matches it was never touched by the writer.
  const lastTopicSeedRef = useRef(initialTopic);
  const lastRecipientSeedRef = useRef(initialRecipient);
  // Apply a pick's seed (see `seed`). The page holds the seed in state,
  // so a new object means a new pick. A field the writer typed in keeps
  // its text; an empty or still-seeded field takes the new client's
  // value, or goes blank when the new client has none.
  useEffect(() => {
    if (seed === null) return;
    const nextTopic = seed.topic ?? '';
    if (topic.trim() === '' || topic === lastTopicSeedRef.current) {
      setTopic(nextTopic);
      lastTopicSeedRef.current = nextTopic;
    }
    const nextRecipient = seed.recipient ?? '';
    if (recipient.trim() === '' || recipient === lastRecipientSeedRef.current) {
      setRecipient(nextRecipient);
      lastRecipientSeedRef.current = nextRecipient;
    }
  }, [seed]);
  const [constraints, setConstraints] = useState('');
  // WS-9: the last failure, as a registry key (errorCopy.ts owns the copy)
  // or a refusal for length with the box it names; null = no error banner;
  // an off-registry key renders the honest needs-platform generic.
  const [failure, setFailure] = useState<AiToolFailure | null>(null);
  const onCeilingRefusal = useInvalidateOnCeilingRefusal(tenantSlug);
  // A refusal for length says the server's number, else the named box's
  // own ceiling as this form reads it now.
  const maxOf = (entry: AiInputTool): number => (entry === 'recipient' ? recipientMaxChars : maxChars);

  // An expired sign-in keeps the brief until a draft exists; after that the
  // page keeps the draft and the brief it came from (src/lib/keptWork.ts).
  // The whole brief: its text and the settings it is written under (the
  // face, unless the viewer's face is fixed, and the tone).
  const kept = useKeptWork<{ recipient: string; topic: string; constraints: string; mode: EmailMode; tone: EmailTone }>(
    keptWorkKey(tenantSlug, 'email-brief', null),
    () =>
      hasDraft || (recipient + topic + constraints).trim() === ''
        ? null
        : { recipient, topic, constraints, mode, tone },
    (k) => {
      setRecipient(k.recipient);
      setTopic(k.topic);
      setConstraints(k.constraints);
      if (!modeLocked) onModeChange(k.mode);
      onToneChange(k.tone);
    },
  );
  useEffect(() => {
    if (hasDraft) kept.clear();
  }, [hasDraft]);

  const isSpice = mode === 'spice';

  const buildSnapshot = (): FormInputSnapshot => {
    const trimmedConstraints = constraints.trim();
    return {
      recipient: recipient.trim(),
      topic: topic.trim(),
      ...(trimmedConstraints ? { constraints: trimmedConstraints } : {}),
      // SPICE family emails have one voice: the request carries Standard
      // there whatever the Care Management control last held (the server
      // reads no tone on the SPICE path). Care Management sends the
      // register the care manager picked.
      tone: isSpice ? 'standard' : tone,
    };
  };

  // The snapshot and the mode are read at the press and travel as the
  // variables: the request and the snapshot filed with the result are one.
  const mutation = useMutation({
    mutationFn: (v: { snapshot: ReturnType<typeof buildSnapshot>; mode: EmailMode }) =>
      aiToolsApi.draftEmail(tenantSlug, { ...v.snapshot, mode: v.mode }),
    onSuccess: (res, v) => {
      setFailure(null);
      onDraftReady(res, v.snapshot, v.mode);
    },
    onError: (e: ApiError, v) => {
      onCeilingRefusal(e);
      // A refusal naming no box reads as the brief's own, Email Content.
      setFailure(aiToolFailureOf(e, 'email_generate', 'topic', v.snapshot, (x) => aiGenerateKeyOf(x) ?? 'unknown'));
    },
  });

  // X.3.5.1 elapsed-time indicator + X.6.4B: report generating state up so the
  // page can render the output "Drafting…" panel.
  const [elapsed, setElapsed] = useState(0);
  const generating = mutation.isPending;
  useEffect(() => {
    onGeneratingChange?.(generating);
    if (!generating) {
      setElapsed(0);
      return;
    }
    setElapsed(0);
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [generating, onGeneratingChange]);

  const recipientTooLong = isTextTooLong(recipient, recipientMaxChars);
  const topicTooLong = isTextTooLong(topic, maxChars);
  const constraintsTooLong = isTextTooLong(constraints, maxChars);
  const canSubmit =
    recipient.trim().length > 0 &&
    topic.trim().length > 0 &&
    !recipientTooLong &&
    !topicTooLong &&
    !constraintsTooLong;
  const failureCopy = failure === null ? null : aiToolFailureCopy(failure, maxOf);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate({ snapshot: buildSnapshot(), mode });
      }}
      className="space-y-[18px]"
      data-testid="email-draft-form"
    >
      {kept.restored && <KeptWorkNotice onDiscard={kept.discard} />}
      {/* Care Management <-> SPICE mode (controlled by the page). Hidden
          when the mode is fixed: a client arrived from a SPICE door, or
          the viewer is SPICE-only and has no other face. */}
      {!modeLocked && (
        <div>
          <EmailModeToggle mode={mode} onChange={onModeChange} disabled={generating} />
        </div>
      )}

      {/* Tone is a Care Management register. SPICE family emails have one
          voice, so the SPICE face has no register to offer and the
          control leaves it. */}
      {!isSpice && <EmailToneSelector tone={tone} onChange={onToneChange} disabled={generating} />}

      <div>
        <label htmlFor="email-recipient" className={LABEL_CLS}>
          Who is this for?
        </label>
        <p className={HELP_CLS}>
          The person&rsquo;s name, e.g. &ldquo;Sarah&rdquo; or &ldquo;Andrew and Eleanor&rdquo;. Sets the greeting and the tone. Not an email address.
        </p>
        <input
          id="email-recipient"
          type="text"
          value={recipient}
          onChange={(e) => setRecipient(e.target.value)}
          disabled={generating}
          placeholder="Sarah, or Andrew and Eleanor"
          className={FIELD_CLS}
        />
        {recipientTooLong && (
          <TextTooLongNote
            surface="email_generate"
            maxChars={recipientMaxChars}
            kind="recipient"
            text={recipient}
            data-testid="email-recipient-too-long"
          />
        )}
      </div>

      <div>
        <label htmlFor="email-topic" className={LABEL_CLS}>
          Email Content
        </label>
        <textarea
          id="email-topic"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          rows={3}
          disabled={generating}
          placeholder="What is this email about?"
          className={FIELD_CLS}
        />
        {topicTooLong && (
          <TextTooLongNote surface="email_generate" maxChars={maxChars} kind="shorten" text={topic} data-testid="email-topic-too-long" />
        )}
      </div>

      <div>
        <label htmlFor="email-constraints" className={LABEL_CLS}>
          Special instructions
          <span className="ml-2 text-xs font-normal text-ink-soft">(optional)</span>
        </label>
        <p className={HELP_CLS}>
          Anything else the AI should know about this email. For example:
          {isSpice ? (
            <>{' '}&ldquo;Keep it under 150 words, and mention the harmonica for next time.&rdquo;</>
          ) : (
            <>{' '}&ldquo;This is a summary of a care plan meeting and needs to be in paragraph form.&rdquo;</>
          )}
        </p>
        <textarea
          id="email-constraints"
          value={constraints}
          onChange={(e) => setConstraints(e.target.value)}
          rows={3}
          disabled={generating}
          placeholder={
            isSpice
              ? 'e.g. keep it under 150 words, mention the harmonica for next time'
              : 'e.g. format this as bullet points, keep it under 100 words, mention the upcoming visit on Tuesday'
          }
          className={FIELD_CLS}
          data-testid="email-constraints"
        />
        {constraintsTooLong && (
          <TextTooLongNote
            surface="email_generate"
            maxChars={maxChars}
            kind="shorten"
            text={constraints}
            data-testid="email-constraints-too-long"
          />
        )}
      </div>

      {failureCopy !== null && (
        <ErrorNotice surface="email_generate" code={failureCopy.key} vars={failureCopy.vars} />
      )}

      <Button
        type="submit"
        disabled={!canSubmit || generating}
        variant={hasDraft ? 'ghost' : 'primary'}
        size={hasDraft ? 'sm' : 'md'}
      >
        <Sparkles size={17} aria-hidden="true" />
        {generating
          ? `Generating your email. This usually takes about 30 seconds…${elapsed > 10 ? ` (${elapsed}s)` : ''}`
          : 'Generate email'}
      </Button>
    </form>
  );
}
